import React, { useState } from "react";
import { FaSort, FaSortDown, FaSortUp } from "react-icons/fa";
import { toTitleCase } from "../../helpers/TitleCase";
import { formatDate } from "../../helpers/dateFormatter";
import { useNavigate } from "react-router-dom";
import { Box, Modal } from "@mui/material";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useDispatch, useSelector } from "react-redux";
import {
  addPredictionPost,
  getUserPrediction,
  getUserPredictionById,
} from "../../redux/actions/pollingAction";

const PredictionTable = ({ data }) => {
  const [sortConfig, setSortConfig] = useState(null);
  const [predictionPostData, setPredictionPostData] = useState({
    description: "",
    prediction_post_Id: null,
  });
  const sortedData = [...(data || [])]?.sort((a, b) => {
    if (!sortConfig) return 0;
    if (a[sortConfig.key] < b[sortConfig.key])
      return sortConfig.direction === "asc" ? -1 : 1;
    if (a[sortConfig.key] > b[sortConfig.key])
      return sortConfig.direction === "asc" ? 1 : -1;
    return 0;
  });
  const dispatch = useDispatch();
  // console.log("sortedData ", sortedData[0]);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const handleOpen = (id, predId) => {
    // console.log("id", id);
    dispatch(getUserPredictionById({ id: id }));
    setPredictionPostData((prev) => ({
      ...prev,
      ["prediction_post_Id"]: predId,
    }));
    setOpen(true);
  };
  const handleClose = () => setOpen(false);

  const handleAddPredictionPost = () => {
    if (predictionPostData?.description !== "") {
      dispatch(addPredictionPost(predictionPostData)).then(() => {
        setOpen(false);
        dispatch(getUserPrediction());
      });
    } else {
      alert("please add description before adding the post");
    }
  };
  const handleChange = (event) => {
    const { name, value } = event.target;
    setPredictionPostData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const requestSort = (key) => {
    let direction = "asc";
    if (
      sortConfig &&
      sortConfig.key === key &&
      sortConfig.direction === "asc"
    ) {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };
  const getSortIcon = (key) => {
    if (!sortConfig || sortConfig.key !== key) return <FaSort />;
    return sortConfig.direction === "asc" ? <FaSortUp /> : <FaSortDown />;
  };

  const { PredictionDetailResponse } = useSelector((state) => state?.pollWeb);
  const transformedData = PredictionDetailResponse?.userPredictionsAverage?.map(
    (item) => ({
      name: item?.["party.abbreviation"]?.toUpperCase(),
      value: parseFloat(parseFloat(item?.averageResults).toFixed(2)),
      color: item?.["party.color_code"],
    })
  );

  return (
    <div>
      <div className="overflow-x-auto">
        {sortedData && sortedData?.length > 0 ? (
          <table className="min-w-full border border-gray-300">
            <thead className="bg-black text-white border-black">
              <tr>
                <th className="px-2 py-4 text-left cursor-pointer text-[12px] font-medium">
                  <div className="flex justify-start gap-2 items-center">
                    Sr. No
                  </div>
                </th>
                <th
                  className="px-2 py-4 text-left cursor-pointer text-[12px] font-medium"
                  onClick={() => requestSort("country")}
                >
                  <div className="flex justify-start gap-2 items-center">
                    Country {getSortIcon("country")}
                  </div>
                </th>
                <th
                  className="px-2 py-4 text-left cursor-pointer text-[12px] font-medium"
                  onClick={() => requestSort("state")}
                >
                  <div className="flex justify-start gap-2 items-center">
                    State {getSortIcon("state")}
                  </div>
                </th>
                <th
                  className="px-2 py-4 text-left cursor-pointer text-[12px] font-medium"
                  onClick={() => requestSort("creationDate")}
                >
                  <div className="flex justify-start gap-2 items-center">
                    Date of Creation {getSortIcon("creationDate")}
                  </div>
                </th>
                <th
                  className="px-2 py-4 text-left cursor-pointer text-[12px] font-medium"
                  onClick={() => requestSort("revisedDate")}
                >
                  <div className="flex justify-start gap-2 items-center">
                    Revised on {getSortIcon("revisedDate")}
                  </div>
                </th>
                <th className="px-2 py-4 text-left text-[12px] font-medium">
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedData?.map((item, idx) => (
                <tr key={item.id} className="bg-white border-b border-gray-300">
                  <td
                    className={`p-2 text-[12px] ${
                      idx % 2 === 0 ? "bg-[#F3F5F6]" : "bg-[#E6E6E6]"
                    }`}
                  >
                    {idx + 1}
                  </td>
                  <td
                    className={`p-2 text-[12px] ${
                      idx % 2 === 0 ? "bg-[#F3F5F6]" : "bg-[#E6E6E6]"
                    }`}
                  >
                    {toTitleCase(item?.prediction?.country?.name)}
                  </td>
                  <td
                    className={`p-2 text-[12px] ${
                      idx % 2 === 0 ? "bg-[#F3F5F6]" : "bg-[#E6E6E6]"
                    }`}
                  >
                    {toTitleCase(item?.state?.name)}
                  </td>
                  <td
                    className={`p-2 text-[12px] ${
                      idx % 2 === 0 ? "bg-[#F3F5F6]" : "bg-[#E6E6E6]"
                    }`}
                  >
                    {formatDate(item?.createdAt)}
                  </td>
                  <td
                    className={`p-2 text-[12px] ${
                      idx % 2 === 0 ? "bg-[#F3F5F6]" : "bg-[#E6E6E6]"
                    }`}
                  >
                    {formatDate(item?.updatedAt)}
                  </td>
                  <td
                    className={`p-2 text-[12px] ${
                      idx % 2 === 0 ? "bg-[#F3F5F6]" : "bg-[#E6E6E6]"
                    }`}
                  >
                    <div className="flex justify-start gap-2">
                      <button
                        className="bg-[#012169] text-white px-5 py-2 rounded-2xl flex items-center"
                        onClick={() =>
                          navigate(
                            `/prediction/my_predictions/${item?.unique_value_for_grouping}`
                          )
                        }
                      >
                        <span className="mr-1">👁</span> View
                      </button>{" "}
                      {!item?.isPublish ? (
                        <button
                          className="bg-[#012169] text-white px-5 py-2 rounded-2xl flex items-center"
                          onClick={() =>
                            handleOpen(
                              item?.unique_value_for_grouping,
                              item?.id
                            )
                          }
                        >
                          {/* {console.log(
                            "item?.id ",
                            item?.unique_value_for_grouping
                          )} */}
                          <span className="mr-1"></span> Publish
                        </button>
                      ) : (
                        <div className="py-2 px-4">Published</div>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <>
            <div className="my-5">
              <div className="text-3xl text-center">Nothing found</div>
              <p className="text-center my-2">Please Make Prediction</p>
            </div>
          </>
        )}

        <Modal open={open} onClose={handleClose}>
          <Box
            className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white p-6 rounded-lg shadow-lg"
            sx={{ maxWidth: 1200, width: "800px" }}
          >
            <div className="flex justify-between items-center gap-4">
              <div className="flex gap-2 items-center">
                <img
                  src={
                    PredictionDetailResponse?.userDetails?.profilePicture ||
                    `https://static.vecteezy.com/system/resources/thumbnails/003/337/584/small/default-avatar-photo-placeholder-profile-icon-vector.jpg`
                  }
                  alt="profile icone"
                  className="rounded-full border  size-[55px] object-cover"
                />
                <p className="text-[16px] capitalize">
                  {PredictionDetailResponse?.userDetails?.firstName ??
                    PredictionDetailResponse?.userDetails?.email}
                </p>
              </div>
              <button
                className="text-[14px] text-white mb-4 bg-[#5F6368] px-7 py-2 rounded-xl"
                onClick={handleClose}
              >
                Close
              </button>
            </div>
            <div className="">
              <ResponsiveContainer width="100%" height={400}>
                <BarChart
                  data={transformedData}
                  margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  {/* <Legend /> */}
                  <Bar dataKey="value" fill="#8884d8" barSize={40}>
                    {transformedData?.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry?.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="my-2">
              <input
                type="text"
                onChange={handleChange}
                name="description"
                className="bg-[#F7F8FA] px-5 pt-2 pb-5 rounded-xl w-full border-gray-200 focus:border focus:outline-none placeholder:text-[12px]"
                placeholder="add description"
              />
              <button
                className="rounded-3xl bg-[#E4002B] mt-2 text-white px-5 py-1 float-end"
                onClick={handleAddPredictionPost}
              >
                Publish
              </button>
            </div>
          </Box>
        </Modal>
      </div>
    </div>
  );
};

export default PredictionTable;
